import { Box, HBox, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { DS } from "../ds/Kit";
import { useOptionalIssuer } from "../issuer/IssuerProvider";
import { getIssuerDisplayName, getIssuerIdentity } from "../issuer/issuerIdentity";

const STATUS_STYLE: Record<TKycStatus, { bg: string; fg: string; border: string; label: string }> = {
	approved: { bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0", label: "Approved" },
	pending: { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A", label: "Pending" },
	under_review: { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A", label: "Under review" },
	declined: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA", label: "Declined" },
	expired: { bg: "#FFFBEB", fg: "#B45309", border: "#FDE68A", label: "Expired" },
	error: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA", label: "Error" },
	none: { bg: DS.soft, fg: DS.muted, border: DS.line, label: "Not started" },
};

const StatusChip = ({ entry }: { entry: IKycIssuerEntry }) => {
	const s = entry.stage === "ready"
		? { bg: DS.accentSoft, fg: DS.accentInk, border: DS.accentLine, label: "Holder details required" }
		: STATUS_STYLE[entry.status] ?? STATUS_STYLE.none;
	return (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: "6px",
				padding: "3px 10px",
				borderRadius: "999px",
				backgroundColor: s.bg,
				color: s.fg,
				border: `1px solid ${s.border}`,
				fontSize: "12px",
				fontWeight: 600,
				whiteSpace: "nowrap",
			}}>
			<Box aria-hidden sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: s.fg }} />{s.label}
		</Box>
	);
};

/**
 * Renders the per-issuer KYC matrix from kyc/issuers. In the user-facing
 * endpoint's issuer_label is the display name. Fall back to the issuer number
 * for older responses and never render an internal provider code.
 * Self-fetches unless `data` is provided.
 */
export const KycIssuersMatrix = ({ data }: { data?: IKycIssuer[] }) => {
	const selectedIssuerId = useOptionalIssuer()?.selectedIssuerId ?? null;
	const [issuers, setIssuers] = useState<IKycIssuer[] | null>(data ?? null);
	const [loading, setLoading] = useState(!data);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (data) {
			setIssuers(data);
			return;
		}
		let active = true;
		(async () => {
			const res = await GD.REQ_KYC_ISSUERS.request({ issuer: selectedIssuerId ?? undefined });
			if (!active) return;
			setLoading(false);
			if (res.error) {
				setError(getFriendlyErrorMessage(res.error, "Could not load issuers"));
				return;
			}
			setIssuers(res.data);
		})();
		return () => {
			active = false;
		};
	}, [data, selectedIssuerId]);

	if (loading) {
		return (
			<Typo sx={{ color: DS.muted, fontSize: "13px" }}>
				Loading issuers…
			</Typo>
		);
	}

	if (error) {
		return (
			<Box role="alert">
				<Typo sx={{ color: "#B91C1C", fontSize: "13px" }}>{error}</Typo>
			</Box>
		);
	}

	if (!issuers || issuers.length === 0) {
		return (
			<Typo sx={{ color: DS.muted, fontSize: "13px" }}>
				No issuers connected yet.
			</Typo>
		);
	}

	return (
		<VBox gap="12px">
			{issuers.map(issuer => {
				const identity = getIssuerIdentity(issuer.issuer);
				const selected = issuer.issuer === selectedIssuerId;
				const entries = Array.isArray(issuer.kyc) ? issuer.kyc : [];
				return (
					<VBox
						key={issuer.issuer}
						gap="0px"
						sx={{
							overflow: "hidden",
							border: `1px solid ${selected ? DS.accentLine : DS.line}`,
							borderRadius: DS.radius,
							background: DS.surface,
							boxShadow: DS.shadow,
						}}>
						<HBox gap="12px" sx={{ alignItems: "center", padding: "14px 18px", background: selected ? DS.accentSoft : DS.soft, borderBottom: `1px solid ${DS.line}` }}>
							<Box sx={{ width: "36px", height: "36px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "11px", color: identity.ink, backgroundColor: identity.surface, border: `1px solid ${identity.border}88`, fontSize: "13px", fontWeight: 700 }}>{issuer.issuer}</Box>
							<Typo sx={{ fontWeight: 650, color: DS.ink, fontSize: "15px", flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" }}>
								{getIssuerDisplayName(issuer)}
							</Typo>
							{selected && (
								<Box sx={{ padding: "2px 10px", borderRadius: "999px", background: DS.accent, color: "#FFFFFF", fontSize: "12px", fontWeight: 600 }}>
									Selected
								</Box>
							)}
						</HBox>
						<VBox gap="0px" sx={{ padding: "0 18px" }}>
							{entries.map((entry, index) => (
								<HBox
									key={entry.kind}
									sx={{ alignItems: "center", justifyContent: "space-between", gap: "12px", minHeight: "52px", borderTop: index ? `1px solid ${DS.line}` : 0 }}>
									<Typo sx={{ color: DS.text, fontSize: "14px", fontWeight: 500, minWidth: 0, overflowWrap: "anywhere" }}>
										{entry.label || entry.kind}
									</Typo>
									<StatusChip entry={entry} />
								</HBox>
							))}
						</VBox>
					</VBox>
				);
			})}
		</VBox>
	);
};
